import React, { Component } from 'react';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { bindActionCreators, compose } from 'redux';
import { withNamespaces } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BoxWithTitle, SelectFormik, TelInput, TagInput } from '../../components/common';
import { companyName, GLOBAL, PanelType, phoneInputPropsTupay } from '../../utils/Constants';
import AuthenticationService from '../../services/AuthenticationService';
import * as GlobalModalActions from '../../redux/actions/GlobalModalActions';
import { showError, showSuccess } from '../../redux/actions/GlobalToastActions';
import { Routes as configRouter } from '../../config/Routes';
import ModalTypes from '../../components/Modal/ModalTypes';
import CircleCheck from '../../assets/img/rich_icons/check-circle.svg';
import LabelR from '../../components/Simple/LabelR';
import LocalStorageHelper from '../../utils/LocalStorageHelper';
import config from '../../envConfig/config';
import { isTupayPanel } from '../../utils/panelUtils';
import { getSignUpButtonClasses } from '../../utils/themeAndStyleHelpers';
import { usePanelType } from '../../hooks/usePanelType';

const TEST_SITE_KEY = config.REACT_APP_RECAPTCHA_SITE_KEY;

class SignUp extends Component {
  constructor(props) {
    super(props);
    this.state = {
      recaptcha: '',
      userEmail: '',
      userPhoneNumber: '',
      userFullName: '',
      userSkypeId: '',
      userComments: '',
      userCompanyName: '',
      userCompanyWebsite: '',
      userTargetMarkets: [],
      userYearsInOperation: '',
      userInContact: '',
      userContact: '',
      enableDataContact: true,
    };
  }

  componentDidMount() {
    const { route } = this.props;
    const referralID = LocalStorageHelper.getCookie('referralID');
    const param = new URLSearchParams(route.location.search).get('referralID');
    if (referralID) {
      if (!param) {
        this.setUserContactData(referralID);
      } else if (param !== referralID) {
        this.setUserContactData(param);
        LocalStorageHelper.storeCookie('referralID', param, 2);
      }
    } else if (param) {
      LocalStorageHelper.storeCookie('referralID', param, 2);
      this.setUserContactData(param);
    }
    if (process.env.REACT_APP_ENV === 'stg') window.location.replace(`${process.env.REACT_APP_WEB_URL_PROD}${configRouter.SIGN_UP}`);
  }

  setUserContactData(referralId) {
    this.setState({
      userInContact: true,
      userContact: referralId,
      enableDataContact: false,
    });
  }

  getInitialValues() {
    return {
      ...this.state,
    };
  }

  getSignUpSuccessLabel = () => {
    const panelType = usePanelType();

    switch (panelType) {
      case PanelType.LIME_PAY:
        return 'labelSignUpSuccessLimepay';
      case PanelType.ONEKEY_PAYMENTS:
        return 'labelSignUpSuccessOnekeyPayments';
      case PanelType.POVILE:
        return 'labelSignUpSuccessPovile';
      case PanelType.TUPAY:
        return 'labelSignUpSuccessTupay';
      case PanelType.GATEWAY_DEPOSIT:
        return 'labelSignUpSuccessSolven';
      case PanelType.ZIMO:
        return 'labelSignUpSuccessZimo';
      default:
        return 'labelSignUpSuccess';
    }
  };

  handleSubmit = (values, actions) => {
    const {
      showError,
      showModal,
      hideModal,
      route: { history },
    } = this.props;

    const formData = {
      email: values.userEmail,
      fullName: values.userFullName,
      phoneNumber: values.userPhoneNumber,
      skypeId: values.userSkypeId,
      companyName: values.userCompanyName,
      companyWebsite: values.userCompanyWebsite,
      targetMarkets: values.userTargetMarkets,
      yearsInOperation: values.userYearsInOperation,
      comments: values.userComments,
      captchaResponse: values.recaptcha,
      inContact: values.userInContact,
      ...(values.userInContact && values.userContact && { contact: values.userContact }),
    };

    const promise = AuthenticationService.signUp(formData);

    promise.then(
      () => {
        actions.setSubmitting(false);
        showModal(
          {
            loadingOnConfirm: false,
            labelConfirm: this.getSignUpSuccessLabel(),
            secondLabelConfirm: 'labelSignUpSuccessSubtitle',
            textBtnConfirm: 'buttonContinue',
            mainIcon: CircleCheck,
            headerIcon: 'check-circle',
            type: 'info',
            title: 'labelSuccessfulRegistration',
            emailConfirmation: true,
            reverseButtons: false,
            disableCancel: true,
            btnConfirmName: 'continue',
            onConfirm: () => {
              hideModal();
              history.push(configRouter.LOGIN);
            },
          },
          ModalTypes.CONFIRMATION_MODAL
        );
      },
      (error) => {
        const { response = {} } = error;
        const msgError = (response.data && response.data.description) || 'labelNetworkError';
        actions.setSubmitting(false);
        showError(msgError);
      }
    );
  };

  getValidationSchema = () => {
    const { t } = this.props;
    return Yup.object().shape({
      userEmail: Yup.string().email(t('validationEmail')).required(t('validationRequired')),
      userFullName: Yup.string()
        .required(t('validationRequired'))
        .min(2, t('validationMinLength', { amount: 2 })),
      userCompanyName: Yup.string()
        .required(t('validationRequired'))
        .min(2, t('validationMinLength', { amount: 2 })),
      userPhoneNumber: Yup.string().test('phoneNumber', 'validationPhoneNumber', (value) => {
        return value !== GLOBAL.INVALID;
      }),
      userCompanyWebsite: Yup.string()
        .required(t('validationRequired'))
        .matches(/((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/, t('validationUrl')),
      userInContact: Yup.boolean().required(t('validationRequired')),
      recaptcha: Yup.string().required(t('validationRequired')).nullable(),
    });
  };

  handleCancel = () => {
    const {
      route: { history },
    } = this.props;
    history.push(configRouter.LOGIN);
  };

  handleMarketsChange = (markets, setFieldValue) => {
    setFieldValue('userTargetMarkets', markets);
  };

  render() {
    const { t } = this.props;
    const { enableDataContact } = this.state;
    const logoUrls = config.REACT_APP_LOGO_URL;
    const { buttonCancel, buttonSave } = getSignUpButtonClasses();
    const telInputProps = isTupayPanel ? phoneInputPropsTupay : {};
    return (
      <main className="root-container">
        <div className="mainContainer signup-background">
          <div className="signup-container">
            <div className="signup-header">
              <div className="col-lg-12">
                <div className="logo-container">
                  <img src={`${logoUrls.defaultLogoSignup}`} alt={companyName} className="logo" />
                </div>
              </div>
            </div>
            <div className="signup-title">
              <h3 className="text-white">{t(`${logoUrls.titleSignUp || 'titleSignUp'}`)}</h3>
            </div>
            <Formik
              initialValues={this.getInitialValues()}
              onSubmit={(values, actions) => {
                this.handleSubmit(values, actions);
              }}
              enableReinitialize
              validationSchema={this.getValidationSchema}
            >
              {({ touched, values, setFieldValue, handleChange, errors, isSubmitting }) => (
                <Form>
                  <BoxWithTitle title={t('labelContactDetails')}>
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="form-group form-item">
                          <LabelR htmlFor="email">{t('columnEmail')}</LabelR>
                          <Field
                            type="email"
                            name="userEmail"
                            className={`form-control ${errors.userEmail && touched.userEmail ? 'is-invalid' : ''}`}
                            placeholder={t('placeholderEmail')}
                          />
                          <ErrorMessage name="userEmail" component="div" className="invalid-feedback" />
                        </div>
                        <div className="form-group form-item">
                          <Field label={t('columnPhoneNumber')} component={TelInput} name="userPhoneNumber" t={t} {...telInputProps} />
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="form-group form-item">
                          <LabelR htmlFor="fullName">{t('labelFullName')}</LabelR>
                          <Field
                            type="fullName"
                            id="fullName"
                            name="userFullName"
                            className={`form-control ${errors.userFullName && touched.userFullName ? 'is-invalid' : ''}`}
                            placeholder={t('placeholderFullName')}
                          />
                          <ErrorMessage name="userFullName" component="div" className="invalid-feedback" />
                        </div>
                        <div className="form-group form-item">
                          <label htmlFor="userSkypeId">{t('labelSkypeId')}</label>
                          <Field type="userSkypeId" id="userSkypeId" name="userSkypeId" className="form-control" placeholder={t('placeholderSkypeId')} />
                        </div>
                      </div>
                    </div>
                  </BoxWithTitle>
                  <BoxWithTitle title={t('labelYourCompany')}>
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="form-group form-item">
                          <LabelR htmlFor="userCompanyName">{t('tabCompany')}</LabelR>
                          <Field
                            type="text"
                            name="userCompanyName"
                            className={`form-control ${errors.userCompanyName && touched.userCompanyName ? 'is-invalid' : ''}`}
                            placeholder={t('placeholderCompany')}
                          />
                          <ErrorMessage name="userCompanyName" component="div" className="invalid-feedback" />
                        </div>
                        <div className="form-group form-item">
                          <label htmlFor="userMarkets">{t('labelTargetMarkets')}</label>
                          <TagInput
                            id="userTargetMarkets"
                            className={`${errors.userTargetMarkets && touched.userTargetMarkets ? 'is-invalid invalid-field' : ''}`}
                            onChange={(value) => this.handleMarketsChange(value, setFieldValue)}
                            value={values.userTargetMarkets}
                            name="userTargetMarkets"
                            placeholder={t('placeholderMarkets')}
                            disableSubmit
                          />
                          <ErrorMessage name="userTargetMarkets" component="div" className="invalid-feedback" />
                        </div>
                        <Field
                          component={SelectFormik}
                          required
                          label={t('labelInContact')}
                          name="userInContact"
                          placeholder={t('placeholderInContact')}
                          isDisabled={!enableDataContact}
                          options={[
                            { value: true, label: t('textYes') },
                            { value: false, label: t('textNo') },
                          ]}
                        />
                        <div className="form-group form-item">
                          <label htmlFor="userComments">{t('labelComments')}</label>
                          <textarea
                            tabIndex={7}
                            type="text"
                            id="userComments"
                            name="userComments"
                            className="form-control"
                            onChange={handleChange}
                            value={values.userComments}
                            placeholder={t('placeholderComments')}
                          >
                            {values.userComments}
                          </textarea>
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="form-group form-item">
                          <LabelR htmlFor="userCompanyWebsite">{t('labelWebSite')}</LabelR>
                          <Field
                            type="userCompanyWebsite"
                            name="userCompanyWebsite"
                            className={`form-control ${errors.userCompanyWebsite && touched.userCompanyWebsite ? 'is-invalid' : ''}`}
                            placeholder={t('placeholderWebsite')}
                          />
                          <ErrorMessage name="userCompanyWebsite" component="div" className="invalid-feedback" />
                        </div>
                        <div className="form-group form-item">
                          <label htmlFor="userYearsInOperation">{t('labelYearsOperation')}</label>
                          <Field type="text" id="userYearsInOperation" name="userYearsInOperation" className="form-control" placeholder={t('placeholderYearsOperation')} />
                        </div>
                        <div className="form-group form-item">
                          <label htmlFor="userContact">{t('labelContact')}</label>
                          <Field
                            type="text"
                            id="userContact"
                            name="userContact"
                            className="form-control"
                            value={values.userContact}
                            placeholder={t('placeholderContactName')}
                            disabled={!values.userInContact || !enableDataContact}
                          />
                          <ErrorMessage name="userContact" component="div" className="invalid-feedback" />
                        </div>
                        <div className="form-group captcha-item text-right">
                          <ReCAPTCHA
                            sitekey={TEST_SITE_KEY}
                            onChange={(response) => {
                              setFieldValue('recaptcha', response);
                            }}
                          />
                          <ErrorMessage name="recaptcha" component="div" className="invalid-feedback" />
                        </div>
                      </div>
                    </div>
                  </BoxWithTitle>
                  <div className="footer">
                    {process.env.REACT_APP_ENV === 'prd' ? (
                      <div className="buttons-container">
                        <button type="button" className={buttonCancel} onClick={() => this.handleCancel()}>
                          {t('buttonCancel')}
                        </button>
                        <button type="submit" className={buttonSave} disabled={isSubmitting}>
                          {isSubmitting && <FontAwesomeIcon icon="spinner" size="lg" spin className="mx-2" />}
                          {t('buttonSave')}
                        </button>
                      </div>
                    ) : (
                      <a href="https://merchants.d24.com/signup" target="_blank" rel="noopener noreferrer">
                        {t('labelFollowLink')}
                      </a>
                    )}
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </main>
    );
  }
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...GlobalModalActions,
      showError,
      showSuccess,
    },
    dispatch
  );

export default compose(withNamespaces(), connect(null, mapDispatchToProps))(SignUp);
